import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bookmark, StickyNote, Highlighter, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function WorkspacePage() {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkspace();
  }, []);

  const fetchWorkspace = async () => {
    try {
      const [bookmarksResponse, notesResponse, highlightsResponse] = await Promise.all([
        api.get('/bookmarks').catch(() => ({ data: [] })),
        api.get('/notes').catch(() => ({ data: [] })),
        api.get('/highlights').catch(() => ({ data: [] })),
      ]);
      
      // Sort by most recent first
      const sortedBookmarks = (bookmarksResponse.data || []).sort((a, b) => 
        new Date(b.bookmark.created_at) - new Date(a.bookmark.created_at)
      );
      const sortedNotes = (notesResponse.data || []).sort((a, b) => 
        new Date(b.note.created_at) - new Date(a.note.created_at)
      );
      const sortedHighlights = (highlightsResponse.data || []).sort((a, b) => 
        new Date(b.highlight.created_at) - new Date(a.highlight.created_at)
      );
      
      setBookmarks(sortedBookmarks);
      setNotes(sortedNotes);
      setHighlights(sortedHighlights);
    } catch (error) {
      console.error('Workspace error:', error);
      toast.error('Failed to load workspace');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Show relative time for recent items
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    // Show full date for older items
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const removeBookmark = async (provisionId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/bookmarks/${provisionId}`);
      setBookmarks((prev) => prev.filter((item) => item.bookmark.provision_id !== provisionId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  const deleteNote = async (noteId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((item) => item.note.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const deleteHighlight = async (highlightId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/highlights/${highlightId}`);
      setHighlights((prev) => prev.filter((item) => item.highlight.id !== highlightId));
      toast.success('Highlight removed');
    } catch (error) {
      toast.error('Failed to remove highlight');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            My Workspace
          </h1>
          <p className="text-slate-600 dark:text-slate-400">Your saved bookmarks, notes, and highlights</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookmarks">
          <TabsList className="mb-6 dark:bg-slate-800">
            <TabsTrigger value="bookmarks" data-testid="bookmarks-tab">
              <Bookmark className="w-4 h-4 mr-2" /> Bookmarks ({bookmarks.length})
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="notes-tab">
              <StickyNote className="w-4 h-4 mr-2" /> Notes ({notes.length})
            </TabsTrigger>
            <TabsTrigger value="highlights" data-testid="highlights-tab">
              <Highlighter className="w-4 h-4 mr-2" /> Highlights ({highlights.length})
            </TabsTrigger>
          </TabsList>

          {/* Bookmarks Tab */}
          <TabsContent value="bookmarks">
            {/* Instructions Card */}
            <Card className="mb-6 border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30">
              <CardContent className="p-6">
                <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">
                  How to Use Workspace Features
                </h3>
                <div className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
                  <div className="flex gap-2 items-start">
                    <Bookmark className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Bookmarks:</span>
                      <span className="ml-1">Click the "Bookmark" button on any provision to save it for quick access later.</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <StickyNote className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Notes:</span>
                      <span className="ml-1">Click "Add Note" on provisions to write your personal insights, reminders, or analysis.</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <Highlighter className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Highlights:</span>
                      <span className="ml-1">Select any text within a provision, then click "Save Highlight" to mark important passages. Your highlights will appear here with yellow background and will be visible inline when you view the provision.</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse dark:bg-slate-800">
                    <CardHeader>
                      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : bookmarks.length === 0 ? (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="p-12 text-center text-slate-600 dark:text-slate-400">
                  <Bookmark className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-600" />
                  <p>No bookmarks yet</p>
                  <p className="text-sm mt-2">Start bookmarking provisions to save them here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookmarks.map((item) => (
                  <Card
                    key={item.bookmark.id}
                    className="hover:shadow-lg transition-all cursor-pointer dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
                    onClick={() => navigate(`/provisions/${item.bookmark.provision_id}`)}
                    data-testid={`bookmark-item-${item.bookmark.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                              {item.reference?.short_name}
                            </p>
                            <span className="text-xs text-slate-400 dark:text-slate-500">•</span>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {formatDate(item.bookmark.created_at)}
                            </p>
                          </div>
                          <CardTitle className="text-xl dark:text-slate-100">{item.section?.title}</CardTitle>
                          <CardDescription className="mt-2 line-clamp-2 dark:text-slate-400">
                            {item.provision?.content}
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => removeBookmark(item.bookmark.provision_id, e)}
                          data-testid={`remove-bookmark-${item.bookmark.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            {/* Instructions Card */}
            <Card className="mb-6 border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30">
              <CardContent className="p-6">
                <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">
                  How to Use Workspace Features
                </h3>
                <div className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
                  <div className="flex gap-2 items-start">
                    <Bookmark className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Bookmarks:</span>
                      <span className="ml-1">Click the "Bookmark" button on any provision to save it for quick access later.</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <StickyNote className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Notes:</span>
                      <span className="ml-1">Click "Add Note" on provisions to write your personal insights, reminders, or analysis.</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <Highlighter className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Highlights:</span>
                      <span className="ml-1">Select any text within a provision, then click "Save Highlight" to mark important passages. Your highlights will appear here with yellow background and will be visible inline when you view the provision.</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse dark:bg-slate-800">
                    <CardHeader>
                      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : notes.length === 0 ? (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="p-12 text-center text-slate-600 dark:text-slate-400">
                  <StickyNote className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-600" />
                  <p>No notes yet</p>
                  <p className="text-sm mt-2">Add notes to provisions to organize your research</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {notes.map((item) => (
                  <Card
                    key={item.note.id}
                    className="hover:shadow-lg transition-all cursor-pointer dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
                    onClick={() => navigate(`/provisions/${item.note.provision_id}`)}
                    data-testid={`note-item-${item.note.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                              {item.reference?.short_name} - {item.section?.title}
                            </p>
                            <span className="text-xs text-slate-400 dark:text-slate-500">•</span>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {formatDate(item.note.created_at)}
                            </p>
                          </div>
                          <CardTitle className="text-lg font-normal text-slate-700 dark:text-slate-300 mb-2">
                            {item.note.content}
                          </CardTitle>
                          <CardDescription className="line-clamp-2 dark:text-slate-400">
                            {item.provision?.content}
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => deleteNote(item.note.id, e)}
                          data-testid={`delete-note-${item.note.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Highlights Tab */}
          <TabsContent value="highlights">
            {/* Instructions Card */}
            <Card className="mb-6 border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30">
              <CardContent className="p-6">
                <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">
                  How to Use Workspace Features
                </h3>
                <div className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
                  <div className="flex gap-2 items-start">
                    <Bookmark className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Bookmarks:</span>
                      <span className="ml-1">Click the "Bookmark" button on any provision to save it for quick access later.</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <StickyNote className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Notes:</span>
                      <span className="ml-1">Click "Add Note" on provisions to write your personal insights, reminders, or analysis.</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <Highlighter className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold">Highlights:</span>
                      <span className="ml-1">Select any text within a provision, then click "Save Highlight" to mark important passages. Your highlights will appear here with yellow background and will be visible inline when you view the provision.</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse dark:bg-slate-800">
                    <CardHeader>
                      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : highlights.length === 0 ? (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="p-12 text-center text-slate-600 dark:text-slate-400">
                  <Highlighter className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-600" />
                  <p>No highlights yet</p>
                  <p className="text-sm mt-2">Select text in provisions and click "Save Highlight" to save them here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {highlights.map((item) => (
                  <Card
                    key={item.highlight.id}
                    className="hover:shadow-lg transition-all cursor-pointer dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
                    onClick={() => navigate(`/provisions/${item.highlight.provision_id}`)}
                    data-testid={`highlight-item-${item.highlight.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                              {item.reference?.short_name} - {item.section?.title}
                            </p>
                            <span className="text-xs text-slate-400 dark:text-slate-500">•</span>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {formatDate(item.highlight.created_at)}
                            </p>
                          </div>
                          <CardTitle className="text-base font-normal">
                            <span className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                              {item.highlight.highlighted_text}
                            </span>
                          </CardTitle>
                          <CardDescription className="mt-2 line-clamp-2 dark:text-slate-400">
                            {item.provision?.content}
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => deleteHighlight(item.highlight.id, e)}
                          data-testid={`delete-highlight-${item.highlight.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <BottomNav />
    </div>
  );
}
